import { Alert, Button, Card, Descriptions, Form, Input, Popconfirm, Space, Table, Typography, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import LmsStatusTag from '@/pages/education/components/LmsStatusTag';
import { lmsErrorMessage, useLmsAccess } from '@/pages/education/utils/lmsAccess';

type Assignment = {
  id: string; title: string; instructions?: string | null; due_on: string; status: string;
  section_name?: string; grade_code?: string; subject_code?: string; subject_name?: string;
};
type Submission = {
  id: string; student_id: string; status: string; submitted_at?: string; body_text?: string | null;
  evidence_url?: string | null; feedback_text?: string | null; feedback_at?: string | null;
  is_late?: number | boolean; admission_no?: string; first_name?: string; last_name?: string;
};
type Detail = { assignment: Assignment; submissions: Submission[]; enrolled_count: number; submitted_count: number };

function FeedbackForm({ submission, onDone }: { submission: Submission; onDone: () => void }) {
  const { t } = useTranslation('education');
  const { canManage } = useLmsAccess();
  const [form] = Form.useForm<{ feedback_text: string }>();
  const send = useMutation({
    mutationFn: (values: { feedback_text: string }) =>
      api.put(`/education/lms/submissions/${submission.id}/feedback`, { feedback_text: values.feedback_text.trim() }),
    onSuccess: () => { message.success(t('lmsFeedbackOk', { defaultValue: 'Feedback returned' })); onDone(); },
    onError: (e) => message.error(lmsErrorMessage(e, 'Unable to return feedback')),
  });
  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      {submission.body_text ? <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{submission.body_text}</Typography.Paragraph> : null}
      {submission.evidence_url ? <a href={submission.evidence_url} target="_blank" rel="noreferrer">{submission.evidence_url}</a> : null}
      {canManage ? (
        <Form form={form} layout="vertical" initialValues={{ feedback_text: submission.feedback_text || '' }} onFinish={(v) => send.mutate(v)}>
          <Form.Item name="feedback_text" label={t('lmsFeedback', { defaultValue: 'Feedback' })} rules={[{ required: true, whitespace: true }]}>
            <Input.TextArea rows={3} maxLength={4000} showCount />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={send.isPending}>
            {t('lmsSendFeedback', { defaultValue: 'Return with feedback' })}
          </Button>
        </Form>
      ) : submission.feedback_text ? (
        <Typography.Text type="secondary">{submission.feedback_text}</Typography.Text>
      ) : null}
    </Space>
  );
}

export default function HomeworkDetailPage() {
  const { t } = useTranslation('education');
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canManage } = useLmsAccess();
  const detail = useQuery({
    queryKey: ['education-lms-homework-detail', id],
    queryFn: async () => (await api.get<Detail>(`/education/lms/homework/${id}`)).data,
  });
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['education-lms-homework-detail', id] });
    queryClient.invalidateQueries({ queryKey: ['education-lms-homework'] });
    queryClient.invalidateQueries({ queryKey: ['education-lms-summary'] });
  };
  const publish = useMutation({
    mutationFn: () => api.post(`/education/lms/homework/${id}/publish`),
    onSuccess: () => { message.success(t('lmsPublishedOk', { defaultValue: 'Published' })); refresh(); },
    onError: (e) => message.error(lmsErrorMessage(e, 'Unable to publish homework')),
  });
  const close = useMutation({
    mutationFn: () => api.post(`/education/lms/homework/${id}/close`),
    onSuccess: refresh,
    onError: (e) => message.error(lmsErrorMessage(e, 'Unable to close homework')),
  });
  const a = detail.data?.assignment;

  return (
    <EducationPageChrome
      title={a?.title || t('lmsHomework', { defaultValue: 'Homework' })}
      description={a ? [a.grade_code, a.section_name, a.subject_name || a.subject_code].filter(Boolean).join(' · ') : undefined}
      tags={a ? <LmsStatusTag status={a.status} /> : undefined}
      extra={<Space wrap>
        <Button onClick={() => navigate('/education/lms/homework')}>{t('lmsHomework', { defaultValue: 'Homework' })}</Button>
        {canManage && a && a.status !== 'closed' ? (
          <Button onClick={() => navigate(`/education/lms/homework/${id}/edit`)}>{t('edit', { defaultValue: 'Edit' })}</Button>
        ) : null}
        {canManage && a?.status === 'draft' ? (
          <Popconfirm title={t('lmsPublish', { defaultValue: 'Publish' })} onConfirm={() => publish.mutate()}>
            <Button type="primary" loading={publish.isPending}>{t('lmsPublish', { defaultValue: 'Publish' })}</Button>
          </Popconfirm>
        ) : null}
        {canManage && a?.status === 'published' ? (
          <Popconfirm title={t('lmsClose', { defaultValue: 'Close' })} onConfirm={() => close.mutate()}>
            <Button loading={close.isPending}>{t('lmsClose', { defaultValue: 'Close' })}</Button>
          </Popconfirm>
        ) : null}
      </Space>}
    >
      {detail.isError ? <Alert type="error" showIcon message={lmsErrorMessage(detail.error, 'Unable to load homework')} /> : null}
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small" loading={detail.isLoading}>
          {a ? (
            <Descriptions column={{ xs: 1, md: 2 }} size="small">
              <Descriptions.Item label={t('lmsDueOn', { defaultValue: 'Due on' })}>{a.due_on}</Descriptions.Item>
              <Descriptions.Item label={t('lmsSubmissions', { defaultValue: 'Submissions' })}>
                {t('lmsSubmissionCount', {
                  defaultValue: 'Submitted: {{submitted}} of {{enrolled}} enrolled',
                  submitted: detail.data?.submitted_count ?? 0,
                  enrolled: detail.data?.enrolled_count ?? 0,
                })}
              </Descriptions.Item>
              <Descriptions.Item label={t('lmsInstructions', { defaultValue: 'Instructions' })} span={2}>
                <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{a.instructions || '—'}</Typography.Paragraph>
              </Descriptions.Item>
            </Descriptions>
          ) : null}
        </Card>
        <Card size="small" title={t('lmsSubmissions', { defaultValue: 'Submissions' })} loading={detail.isLoading}>
          <Table
            rowKey="id" size="small" pagination={{ pageSize: 25 }}
            dataSource={detail.data?.submissions || []}
            locale={{ emptyText: t('lmsNoRows', { defaultValue: 'Nothing here yet' }) }}
            expandable={{
              expandedRowRender: (row: Submission) => <FeedbackForm submission={row} onDone={refresh} />,
            }}
            columns={[
              { title: t('admissionNo', { defaultValue: 'Admission no.' }), dataIndex: 'admission_no', width: 140 },
              { title: t('student', { defaultValue: 'Student' }), render: (_: unknown, r: Submission) => [r.first_name, r.last_name].filter(Boolean).join(' ') },
              { title: t('lmsSubmitted', { defaultValue: 'Submitted' }), dataIndex: 'submitted_at', render: (v?: string) => (v ? String(v).slice(0, 16).replace('T', ' ') : '—') },
              {
                title: t('status', { defaultValue: 'Status' }), width: 160,
                render: (_: unknown, r: Submission) => (
                  <Space size={4}>
                    <LmsStatusTag status={r.status} />
                    {r.status === 'returned' && Number(r.is_late) === 1 ? <LmsStatusTag status="late" /> : null}
                  </Space>
                ),
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
